import { Money } from "@shopify/hydrogen-react";
import { cn } from "@ui/lib/utils";
import { useTranslation } from "react-i18next";
import type { CurrencyCode } from "@shopify/hydrogen-react/storefront-api-types";

// Base price (bazna cijena) from variant metafield custom.sidrena_cijena,
// shown next to every price from 17.11.2026 (Odluka NN 101/2026 as amended by
// NN 110/2026; ZZP art. 7). Labelled so it can't be mistaken for the price.

type BasePriceMetafield = { value?: string | null } | null | undefined;

export function parseBasePrice(metafield: BasePriceMetafield) {
  if (!metafield?.value) return null;
  const { amount, currency_code } = JSON.parse(metafield.value) as {
    amount: string;
    currency_code: CurrencyCode;
  };
  return { amount, currencyCode: currency_code };
}

// Cards show the lowest price, so they show the base price of the cheapest
// variant.
export function cheapestVariantBasePrice(
  variants: ReadonlyArray<{
    price: { amount?: unknown };
    basePrice?: BasePriceMetafield;
  }>,
) {
  if (!variants.length) return null;
  const minPrice = Math.min(...variants.map((v) => Number(v.price.amount)));
  const bases = variants
    .filter((v) => Number(v.price.amount) === minPrice)
    .map((v) => parseBasePrice(v.basePrice))
    .filter((b) => b !== null);
  if (!bases.length) return null;
  return bases.reduce((min, b) =>
    Number(b.amount) < Number(min.amount) ? b : min,
  );
}

export function BasePrice({
  metafield,
  className,
}: Readonly<{ metafield: BasePriceMetafield; className?: string }>) {
  return (
    <BasePriceLine
      basePrice={parseBasePrice(metafield)}
      className={className}
    />
  );
}

export function BasePriceLine({
  basePrice,
  className,
}: Readonly<{
  basePrice: { amount: string; currencyCode: CurrencyCode } | null;
  className?: string;
}>) {
  const { t } = useTranslation();
  if (!basePrice) return null;
  return (
    <p className={cn("text-tny text-text-3 tabular-nums", className)}>
      {t("Common.basePrice")}:{" "}
      <Money data={basePrice} as="span" className="inline" />
    </p>
  );
}
